import { mergeClassNames } from "../../utils/classes";
import Icon from "../Icon/Icon";

import type { FontelloIcons } from "../../assets/fontello";
import type { MouseEventHandler, ReactNode } from "react";

import * as styles from "./badge.module.scss";

export type BadgeColor =
	| "active"
	| "activeDark"
	| "draft"
	| "credit"
	| "rejected"
	| "errorLight"
	| "overdue"
	| "attention"
	| "amend"
	| "complete"
	| "archived"
	| "ghost"
	| "campaign"
	| "agency";

export type BadgeProps = {
	color: BadgeColor;
	name: ReactNode;
	title?: string;
	className?: string;
	noMargin?: boolean;
	testId?: string;
	iconTestId?: string;
	stopPropagation?: boolean;
	onClick?: () => void;
	iconOnClick?: () => void;
} & ({ icon: FontelloIcons; iconPosition: "left" | "right" } | { icon?: undefined; iconPosition?: undefined });

export const Badge = ({
	color,
	name,
	title,
	className,
	noMargin,
	onClick,
	iconOnClick,
	testId,
	iconTestId,
	stopPropagation,
	icon,
	iconPosition,
}: BadgeProps) => {
	const classNames = mergeClassNames(
		styles.status,
		styles[color],
		className,
		onClick && styles.clickable,
		noMargin && styles.noMargin,
	);

	const renderIcon = (iconName?: FontelloIcons) => {
		return (
			iconName && (
				<span
					className={mergeClassNames(
						name == null || name == "" ? undefined : iconPosition === "left" ? styles.leftIcon : styles.rightIcon,
						iconOnClick ? styles.clickableIcon : "",
					)}
					onClick={iconOnClick}
					data-testid={iconTestId}
				>
					<Icon name={iconName} noMargin size="16" />
				</span>
			)
		);
	};

	const handleClick: MouseEventHandler = e => {
		if (onClick) {
			stopPropagation && e.stopPropagation();
			onClick();
		}
	};

	return (
		<span className={classNames} title={title} data-testid={testId} onClick={handleClick}>
			{iconPosition === "left" && renderIcon(icon)}
			{name}
			{iconPosition === "right" && renderIcon(icon)}
		</span>
	);
};
